import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BLOCKS_LOTTERY_ENABLED,
  BLOCKS_PURCHASE_TOMAN,
  BLOCKS_TELEGRAM_CHANNEL,
} from "../../../config/blocks";
import type { BlocksBalanceInfo, BlocksExperienceSnapshot } from "./types";
import { buildFallbackBlocksExperienceSnapshot } from "./fallbackSnapshot";

type FetchState = "idle" | "loading" | "error" | "success";

interface BlocksExperienceState {
  snapshot: BlocksExperienceSnapshot | null;
  fetchState: FetchState;
  errorMessage: string | null;
  refresh: () => Promise<void>;
}

const API_PATH = "/api/lottery-experience";

const PublicBlocksExperienceContext = createContext<BlocksExperienceState | null>(null);

const PERSIAN_DIGITS: Record<string, string> = {
  "۰": "0",
  "۱": "1",
  "۲": "2",
  "۳": "3",
  "۴": "4",
  "۵": "5",
  "۶": "6",
  "۷": "7",
  "۸": "8",
  "۹": "9",
  "٠": "0",
  "١": "1",
  "٢": "2",
  "٣": "3",
  "٤": "4",
  "٥": "5",
  "٦": "6",
  "٧": "7",
  "٨": "8",
  "٩": "9",
};

function normaliseNumericInput(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "bigint") {
    return Number(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const digits = trimmed
      .replace(/[,_\s\u066C\u200C]/g, "")
      .replace(/\u066B/g, ".")
      .replace(/[۰-۹٠-٩]/g, (char) => PERSIAN_DIGITS[char] ?? char);
    if (!digits) {
      return null;
    }
    const parsed = Number(digits);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normaliseInteger(value: unknown): number {
  const numeric = normaliseNumericInput(value);
  if (numeric == null) {
    return 0;
  }
  const truncated = Math.trunc(numeric);
  return Number.isFinite(truncated) ? truncated : 0;
}

function normaliseTimestamp(value: unknown): number | null {
  const numeric = normaliseNumericInput(value);
  if (numeric == null) {
    return null;
  }
  const truncated = Math.trunc(numeric);
  return Number.isFinite(truncated) ? Math.max(0, truncated) : null;
}

function normaliseBalance(balance: BlocksBalanceInfo): BlocksBalanceInfo {
  return {
    availableToman: normaliseInteger(balance.availableToman),
    requiredToman: normaliseInteger(balance.requiredToman),
    pendingTopUpToman: normaliseInteger(balance.pendingTopUpToman),
    lotteryEarningsSats: normaliseInteger(balance.lotteryEarningsSats),
    referralEarningsSats: normaliseInteger(balance.referralEarningsSats),
    withdrawableSats: normaliseInteger(balance.withdrawableSats),
    pendingWithdrawalSats: normaliseInteger(balance.pendingWithdrawalSats),
    lastWithdrawalRequestedAt: normaliseTimestamp(balance.lastWithdrawalRequestedAt),
    nextWithdrawalEligibleAt: normaliseTimestamp(balance.nextWithdrawalEligibleAt),
  };
}

function usePublicBlocksExperienceInternal(enabled: boolean): BlocksExperienceState {
  const [snapshot, setSnapshot] = useState<BlocksExperienceSnapshot | null>(() => {
    // Nothing renders the snapshot while the lottery is off, so skip building the fallback too.
    if (!enabled) {
      return null;
    }
    try {
      return buildFallbackBlocksExperienceSnapshot();
    } catch (error) {
      console.warn("[lottery-experience] initial fallback snapshot build failed", error);
      return null;
    }
  });
  const [fetchState, setFetchState] = useState<FetchState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasAttemptedFetchRef = useRef(false);

  const fetchSnapshot = useCallback(async () => {
    hasAttemptedFetchRef.current = true;

    // With the lottery switched off nothing renders this snapshot, so skip the request entirely
    // rather than hitting /api/lottery-experience on every page load.
    if (!enabled) {
      setSnapshot(null);
      setErrorMessage(null);
      setFetchState("idle");
      return;
    }

    setFetchState("loading");
    setErrorMessage(null);

    const isTelegramWebApp = (() => {
      try {
        return typeof window !== "undefined" && Boolean((window as any)?.Telegram?.WebApp);
      } catch {
        return false;
      }
    })();

    const doRequest = async (timeoutMs: number): Promise<BlocksExperienceSnapshot> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), Math.max(1000, timeoutMs));
      try {
        const params = new URLSearchParams({ _: String(Date.now()) });
        const url = `${API_PATH}?${params.toString()}`;
        const headers: Record<string, string> = { Accept: "application/json" };

        // Attach Telegram login session if available to personalise referral stats for the viewer.
        try {
          if (typeof window !== "undefined" && window.localStorage) {
            const raw = window.localStorage.getItem("lottery.telegramLoginState");
            if (raw) {
              const parsed = JSON.parse(raw) as { token?: unknown; user?: { id?: unknown } | null };
              const token = typeof parsed?.token === "string" ? parsed.token.trim() : null;
              const userId = (() => {
                const rawId = parsed?.user?.id as unknown;
                if (typeof rawId === "number" && Number.isFinite(rawId)) return Math.trunc(rawId);
                if (typeof rawId === "string" && rawId.trim()) {
                  const n = Number(rawId.trim());
                  if (Number.isFinite(n)) return Math.trunc(n);
                }
                return null;
              })();
              if (token && userId != null) {
                headers["x-lottery-login-token"] = token;
                headers["x-telegram-user-id"] = String(userId);
              }
            }
          }
        } catch {
          // ignore localStorage issues
        }

        const response = await fetch(url, { signal: controller.signal, headers });
        if (!response.ok) {
          throw new Error(`lottery_api_${response.status}`);
        }
        return (await response.json()) as BlocksExperienceSnapshot;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      // First attempt (4s default, 8s in Telegram Mini App)
      const firstTimeout = isTelegramWebApp ? 8000 : 4000;
      const payload = await doRequest(firstTimeout);
      setSnapshot(payload);
      setFetchState("success");
    } catch (firstError) {
      console.warn("[lottery-experience] first fetch failed, retrying once", firstError);
      try {
        // Second attempt with slightly longer timeout (6s default, 10s in Telegram Mini App)
        const retryTimeout = isTelegramWebApp ? 10000 : 6000;
        const payload = await doRequest(retryTimeout);
        setSnapshot(payload);
        setFetchState("success");
      } catch (error) {
        console.error("[lottery-experience] fetch failed", error);
        try {
          const fallbackSnapshot = buildFallbackBlocksExperienceSnapshot();
          setSnapshot(fallbackSnapshot);
        } catch (fallbackError) {
          console.warn("[lottery-experience] fallback snapshot build failed", fallbackError);
        }
        setFetchState("error");
        const message =
          "دریافت اطلاعات زنده با خطا مواجه شد؛ نمایش اطلاعات نمونه تا بازگشت ارتباط برقرار شد.";
        setErrorMessage(message);
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!hasAttemptedFetchRef.current) {
      fetchSnapshot();
    }
  }, [fetchSnapshot]);

  const enrichedSnapshot = useMemo(() => {
    if (!snapshot) {
      return null;
    }

    const sortedBlocks = [...snapshot.blocks].sort((a, b) => b.height - a.height);
    const balance = normaliseBalance(snapshot.balance);
    const publicEnvironment = snapshot.publicEnvironment ?? {
      purchaseAmountToman: BLOCKS_PURCHASE_TOMAN,
      purchasesActive: true,
      withdrawalMinimumSats: 500,
      withdrawalCooldownHours: 7 * 24,
      telegramChannel: BLOCKS_TELEGRAM_CHANNEL,
    };

    const viewer = snapshot.viewer
      ? {
          ...snapshot.viewer,
          channelMember:
            typeof snapshot.viewer.channelMember === "boolean"
              ? snapshot.viewer.channelMember
              : null,
        }
      : {
          isLoggedIn: false,
          telegramVerified: false,
          channelMember: null,
        };

    return { ...snapshot, blocks: sortedBlocks, viewer, publicEnvironment, balance };
  }, [snapshot]);

  const state = useMemo<BlocksExperienceState>(
    () => ({
      snapshot: enrichedSnapshot,
      fetchState,
      errorMessage,
      refresh: fetchSnapshot,
    }),
    [enrichedSnapshot, fetchState, errorMessage, fetchSnapshot],
  );

  return state;
}

export function PublicBlocksExperienceProvider({
  children,
  enabled = BLOCKS_LOTTERY_ENABLED,
}: {
  children: ReactNode;
  enabled?: boolean;
}) {
  const value = usePublicBlocksExperienceInternal(enabled);
  return (
    <PublicBlocksExperienceContext.Provider value={value}>
      {children}
    </PublicBlocksExperienceContext.Provider>
  );
}

export function usePublicBlocksExperience(): BlocksExperienceState {
  const contextValue = useContext(PublicBlocksExperienceContext);
  if (contextValue) {
    return contextValue;
  }
  return usePublicBlocksExperienceInternal(true);
}
